import nameModelValidator from 'forms/shared/validators/models/name/name';
import phoneModelValidator from 'forms/shared/validators/models/phone/phone';
import locationBirthplaceModelValidator from 'forms/shared/validators/models/location/models/birthplace';
import locationAddressModelValidator from 'forms/shared/validators/models/location/models/address';
import physicalAddress from 'forms/shared/validators/models/physicalAddress/physicalAddress';
import relationshipsSharedForeignBornDocumentValidator from 'forms/sections/relationships/shared/foreignBornDocument';
import { hasYesOrNo } from 'models/validate';
import { DEFAULT_EARLIEST, DEFAULT_LATEST, OTHER } from 'constants/dateLimits';
import { isInternational, isPO } from 'helpers/location';
import { sortSafeDateTimes, getSafeDateTime, setToStartOfMonth } from 'helpers/date';
import { ValidateJSModel, BranchCollectionModel } from 'forms/shared/types';
import {
  RelationshipsCivilUnionModel,
  RelationshipsCivilUnionOtherNamesModel,
} from 'forms/sections/relationships/marital/types';
import usCityStateZipInternationalCity from 'forms/shared/validators/models/location/models/usCityStateZipInternationalCity';
import { DateBaseValidatorOptions } from 'forms/shared/validators/base/date/types';
import { SanitizeDateRange } from 'forms/shared/validators/models/dateRange/instance';
import { YES } from 'constants/values';
import { isPVQForm } from 'helpers/form';
import pvqNameModelValidator from 'forms/shared/validators/models/pvqName/name';
import { DeepSafePartial } from 'types/types';
import pvqLocationModelValidator from 'forms/shared/validators/models/location/pvqLocation';
import { isSafeUnitedStates } from 'helpers/locations';
import { otherNamesOptions } from 'constants/enums/relationshipOptions';
import emailModelValidator from 'forms/shared/validators/models/pvqEmail';
import { countryString } from '../../../../../validators/location';
import relationshipsSharedForeignBornDocumentCivilValidator from '../../shared/foreignBornDocumentCivil';

export const hasAliasDuplicates = (value: DeepSafePartial<BranchCollectionModel<RelationshipsCivilUnionOtherNamesModel>>) => {
  let result = false;
  if (value?.items) {
    const cleanItems = value.items.map((x) => x?.Item);
    // remove the type pollution for validation from the date range
    // there may be more type pollution possible that wasn't available during the testing
    cleanItems.forEach((v) => {
      const today = new Date();

      if (v) {
        const dates = SanitizeDateRange(v?.DatesUsed);
        if (dates?.present && dates?.to) {
          dates.to.day = today.getDate().toString();
          dates.to.year = today.getFullYear().toString();
          dates.to.month = (today.getMonth() + 1).toString();
        }
        v.DatesUsed = { ...dates }; // eslint-disable-line no-param-reassign
      }
    });
    // compare items on the list case insensitive and ignore Reason, Has, and index differences.
    const items = cleanItems.map((x) => JSON.stringify({
      ...x, Reason: '', index: '', Has: ''
    }).toLowerCase());
    const uniqueItems = new Set(items);
    result = items.length !== uniqueItems.size;
  }
  return result;
};

export const relationshipsMaritalCivilUnionOtherName: ValidateJSModel<RelationshipsCivilUnionOtherNamesModel> = {
  Has: {},
  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        model: { validator: pvqNameModelValidator },
      };
    }
    return {
      presence: true,
      model: { validator: nameModelValidator },
    };
  },
  NameOptions: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: {
          validator: {
            inclusion: {
              within: otherNamesOptions
            }
          },
        },
      };
    }

    return {};
  },
  NameOptionsExplanation: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType) && attributes?.NameOptions?.value === 'Other') {
      return {
        hasValue: true,
      };
    }

    return {};
  },
  MaidenName: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    return {
      presence: true,
      hasValue: true
    };
  },
  DatesUsed: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.DatesNotApplicable?.applicable !== false) {
        return {
          presence: true,
          daterange: {
            earliest: setToStartOfMonth(options?.earliest || DEFAULT_EARLIEST),
            latest: DEFAULT_LATEST,
            tooEarly: 'error.marital.min',
          },
        };
      } return {};
    }
    return {
      presence: true,
      daterange: {
        earliest: setToStartOfMonth(options?.earliest),
        latest: DEFAULT_LATEST,
        tooEarly: 'error.marital.min',
      },
    };
  },
};

export const relationshipsMaritalCivilUnionValidator: ValidateJSModel<RelationshipsCivilUnionModel> = {
  EmailNotApplicable: {},

  UseApplicantPhoneNotApplicable: {},

  UseCurrentAddress: {},

  AddressSeparatedNotApplicable: {},

  Name: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        model: { validator: pvqNameModelValidator },
      };
    }
    return {
      presence: true,
      model: { validator: nameModelValidator },
    };
  },

  Birthdate: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        date: {
          earliest: DEFAULT_EARLIEST,
          tooEarly: 'error.birthdateTooFar.min',
          latest: DEFAULT_LATEST,
        }
      };
    }
    return {
      presence: true,
      date: { ...OTHER, tooEarly: 'error.birthdateTooFar.min' },
    };
  },

  BirthPlace: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        location: {
          validator: locationBirthplaceModelValidator,
        },
      };
    }
    return {
      presence: true,
      location: { validator: locationBirthplaceModelValidator, requireCityOrCounty: true },
    };
  },

  Citizenship: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        hasValue: true,
        country: true,
      };
    }
    return {
      presence: true,
      country: true,
      hasValue: true,
    };
  },

  ForeignBornDocumentCivil: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        model: {
          validator: relationshipsSharedForeignBornDocumentCivilValidator,
          spouseBirthdate: attributes?.Birthdate
        },
      };
    }

    return {};
  },

  SSN: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        ssn: true,
      };
    }
    return {
      presence: true,
      ssn: true,
    };
  },

  OtherNames: (value, attributes, attributeName, options) => {
    const dateLimits: DateBaseValidatorOptions = {
      earliest: DEFAULT_EARLIEST,
      latest: DEFAULT_LATEST,
    };
    if (attributes?.Birthdate) { dateLimits.earliest = attributes?.Birthdate; }
    if (hasAliasDuplicates(value)) {
      return {
        presence: true,
        length: { is: -2, message: 'error.civilunion.duplicateAlias' },
        branchCollection: {
          validator: relationshipsMaritalCivilUnionOtherName,
          ...dateLimits,
        },
      };
    }
    return {
      presence: true,
      branchCollection: {
        validator: relationshipsMaritalCivilUnionOtherName,
        ...dateLimits,
      },
    };
  },

  EnteredCivilUnion: (value, attributes, attributeName, options) => {
    const { Birthdate } = attributes;
    const { applicantBirthdate } = options;

    const sortedDates = sortSafeDateTimes([
      getSafeDateTime(Birthdate),
      getSafeDateTime(applicantBirthdate),
    ]);

    const earliestDate = sortedDates.length ? sortedDates[sortedDates.length - 1] : null;

    if (isPVQForm(options?.formType)) {
      if (options?.maritalStatus === 'Married' || options?.maritalStatus === 'Spouse' || options?.maritalStatus === 'Separated') {
        return {
          presence: true,
          date: {
            earliest: earliestDate?.datetime,
            latest: DEFAULT_LATEST,
          },
        };
      }

      return {};
    }

    return {
      presence: true,
      date: {
        earliest: earliestDate?.datetime,
        latest: DEFAULT_LATEST,
      },
    };
  },

  Location: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (['Married', 'Separated'].includes(options?.maritalStatus || '')) {
        return {
          presence: true,
          location: {
            validator: pvqLocationModelValidator,
            requireStreet: false,
            requireIsMilitaryDiplomaticAddress: false,
            requireCounty: true,
            requireZipCode: false
          }
        };
      }

      return {};
    }
    return {
      presence: true,
      location: { validator: locationBirthplaceModelValidator, requireCityOrCounty: true },
    };
  },

  SameAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo }
      };
    }
    return {};
  },

  Telephone: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.UseApplicantPhoneNotApplicable?.applicable !== false) {
        return {
          presence: true,
          model: {
            validator: phoneModelValidator,
            requireNumber: true,
            requireNumberType: true
          }
        };
      }
    }
    // UseApplicantPhoneNotApplicable applicable is the opposite of all other NotApplicable patterns
    if (attributes?.UseApplicantPhoneNotApplicable?.applicable === false) { return {}; }

    return { presence: true, model: { validator: phoneModelValidator, requireNumber: true } };
  },

  Email: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.EmailNotApplicable?.applicable !== false) {
        return {
          presence: true,
          model: {
            validator: emailModelValidator,
            requireEmailType: false,
          }
        };
      }
    }
    if (attributes?.EmailNotApplicable && attributes?.EmailNotApplicable.applicable === false) {
      return {};
    }

    return {
      hasValue: { validator: { email: true } },
    };
  },

  DateSeparated: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options?.maritalStatus === 'Separated') {
        const dateLimits: DateBaseValidatorOptions = {
          earliest: DEFAULT_EARLIEST,
          latest: DEFAULT_LATEST,
        };
        if (attributes?.EnteredCivilUnion) {
          dateLimits.earliest = attributes?.EnteredCivilUnion;
          dateLimits.tooEarly = 'error.dateSeparated.min';
        }
        return { presence: true, date: dateLimits };
      }
    }
    if (attributes?.Separated && attributes?.Separated.value === 'Yes') {
      const dateLimits: DateBaseValidatorOptions = {
        earliest: DEFAULT_EARLIEST,
        latest: DEFAULT_LATEST,
      };
      if (attributes?.EnteredCivilUnion) {
        dateLimits.earliest = attributes?.EnteredCivilUnion;
        dateLimits.tooEarly = 'error.dateSeparated.min';
      }

      return { presence: true, date: dateLimits };
    }

    return {};
  },

  AddressSeparated: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options?.maritalStatus === 'Separated') {
        return {
          presence: true,
          location: {
            validator: pvqLocationModelValidator,
            requireStreet: isSafeUnitedStates(value),
          }
        };
      }
    }
    if (
      attributes?.AddressSeparatedNotApplicable
      && attributes?.AddressSeparatedNotApplicable.applicable === false
    ) {
      return {};
    }

    if (attributes?.Separated && attributes?.Separated.value === 'Yes') {
      return {
        presence: true,
        location: { validator: usCityStateZipInternationalCity },
      };
    }

    return {};
  },

  GovOffice: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (options?.maritalStatus === 'Separated') {
        return {
          hasValue: true
        };
      }
    }

    return {};
  },

  Separated: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    const shouldValidate = ['Married', 'Separated'].includes(options?.maritalStatus || '');
    const validator = options?.maritalStatus === 'Separated' ? {
      inclusion: {
        within: [YES],
        message: 'error.invalidSeparatedStatusSelection'
      },
    } : hasYesOrNo;
    return shouldValidate ? {
      presence: true,
      hasValue: { validator },
    } : {};
  },

  Address: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    // UseCurrentAddress applicable is the opposite of all other NotApplicable patterns
    if (attributes?.UseCurrentAddress?.applicable === true) { return {}; }

    return { presence: true, location: { validator: locationAddressModelValidator } };
  },

  SpouseAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (attributes?.SameAddress?.value !== 'Yes') {
        return {
          presence: true,
          location: {
            validator: pvqLocationModelValidator,
            requireStreet: isSafeUnitedStates(value),
            requireIsMilitaryDiplomaticAddress: true,
            requireMilitaryDiplomaticInstallationName: true,
            requireApoFpoDpoPostcode: !isSafeUnitedStates(value),
          }
        };
      }
    }

    return {};
  },

  AlternateAddress: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    // if "use my address" is checked or address (country) is not filled out, do not validate for Alternate address
    if (attributes?.UseCurrentAddress?.applicable || !attributes?.Address?.country) {
      return {};
    }

    if (attributes?.Address && isInternational(attributes?.Address)) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: true,
          hasTelephone: false,
        },
      };
    }

    if (attributes?.Address && isPO(attributes?.Address)) {
      return {
        presence: true,
        model: {
          validator: physicalAddress,
          militaryAddress: false,
          hasTelephone: false,
        },
      };
    }

    return {};
  },

  Divorced: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        hasValue: { validator: hasYesOrNo },
      };
    }
    return {
      presence: true,
      hasValue: true,
    };
  },

  ForeignBornDocument: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      return {};
    }
    if (attributes?.BirthPlace && attributes?.BirthPlace.country
      && countryString(attributes?.BirthPlace.country) !== 'United States'
    ) {
      return {
        presence: true,
        model: {
          validator: relationshipsSharedForeignBornDocumentValidator,
          spouseBirthdate: attributes?.Birthdate
        },
      };
    }

    return {};
  },

  Comments: {},
  ContinueComments: {},
  SeparatedComments: {},
  SeparationComments: {},
  AnswerComments: {},
  AddressComments: {},
  PhysicalLocationComments: {},
};
